import { isMarketSol } from '@packages/locale/utils/market.ts'
import type { Market } from '@packages/locale/types/locales.ts'

import type { Trip } from '@layers/web/stores/types/trip.ts'
import type { ProductArea } from '@layers/web/utils/types/product-area.ts'
import type { HashedIdentity } from '@layers/web/utils/types/tracking.ts'
import { normalizeCategories, getTrackingContent, tripInfoToProductParams, tripInfoToPurchaseItems } from '@layers/web/utils/tracking.ts'
import { getMetaMatchParameters } from '@layers/web/utils/meta-click-id.ts'
import { classifyProductArea, getItemBrandForProductArea } from '@layers/web/utils/product-area.ts'
import { getEmptyValuesKeys } from '@layers/web/utils/objects.ts'
import type { Category } from '@layers/web/services/types/category-api.ts'
import type {
  PhoneNumber,
  IdentifyUserOptions,
  ProductEventParams,
  BookingProductEventParams,
  PurchaseEventParams,
  PageViewEventParams,
  FavouriteEventParams,
} from '@layers/web/composables/types/tracking.ts'

/**
 * TRACKING STRATEGY FOR TRAVEL PRODUCTS
 *
 * Product ID Strategy (REVISED):
 * - No airport OR airport === "DYN" (dynamic):
 *   - content_type = "product_group"
 *   - id = trip_type_id (e.g., "14")
 * - Airport selected (specific airport code):
 *   - content_type = "product"
 *   - id = trip_type_id + "-" + airport (e.g., "14-STO")
 * - Bus trips: content_type = "product" always
 * - Departure date: Used as content_variant/item_variant for add-to-cart/purchase/initiate-checkout
 *
 * Tracking Events:
 * - ViewContent: Track when user views a trip/destination (Customer.io: product_viewed)
 * - AddToCart: Track when user adds trip to cart/selection (Customer.io: product_added)
 * - InitiateCheckout: Track when user starts booking process (Customer.io: checkout_started)
 * - Purchase: Track completed bookings (Customer.io: purchased)
 *
 * Categories: Added to all events (Facebook, Google Analytics, Customer.io)
 *
 * To AI:
 * When doing changes here, don't forget to update docs/web/tracking-marketing.md and use md-to-pdf
 * to generate pdf file that you then replace the current document that marketing uses:
 * https://app.notion.com/p/rolfsbuss/115b13f9ec7242af9d9df0ffd4de2f47?p=3c68502cbfe28019bba3f44dd800540d
 */
export const useTracking = () => {
  const { $sentryCaptureMessage } = useNuxtApp()

  const {
    public: {
      ADTRACTION,
      FB_ID,
      defaultPhoneCountryCode,
    },
  } = useRuntimeConfig()

  const rootStore = useRootStore()
  const {
    agentCookie,
    isUserReseller,
  } = storeToRefs(rootStore)

  const localeStore = useLocaleStore()
  const {
    locale,
    urls: localeURLs,
  } = storeToRefs(localeStore)

  const dayjs = useDayjs()

  const { userConsentStatus } = useUserConsent()

  const { incrementModeSwitchCount, registerPageView } = useProductArea()

  /**
   * Product area for the booking-flow ecommerce events (add_to_cart, begin_checkout,
   * purchase), derived from the booking's `market` passed in by the caller. Kept
   * caller-supplied so this composable stays decoupled from the booking store; the
   * caller knows the market (route/store on the book page, the confirmation response
   * on the purchase page).
   */
  const productAreaForMarket = (market: Market): ProductArea =>
    isMarketSol(market) ? 'solresor' : 'gruppresor'

  const tripToTrackingParams = (trip: Trip) => {
    const tripTrackingParams: {
      uri?: string
      imageUri?: string
      description?: string
      minimumPrice?: number
      minimumPriceWithoutDiscount?: number
      isDiscounted?: boolean
    } = {}

    if (trip.url) {
      tripTrackingParams.uri = (localeURLs.value.BASE_URL + trip.url)
    }

    if (trip.main_picture_cloudinary) {
      tripTrackingParams.imageUri = formatRolfsImagePath(trip.main_picture_cloudinary)
    }

    if (trip.short_preamble) {
      tripTrackingParams.description = stripHTML(trip.short_preamble)
    }

    if (trip.price_min) {
      tripTrackingParams.minimumPrice = trip.price_min

      if (trip.price_min_without_discount) {
        Object.assign(
          tripTrackingParams,
          {
            minimumPriceWithoutDiscount: trip.price_min_without_discount,
            isDiscounted: trip.price_min !== trip.price_min_without_discount,
          }
        )
      }
    }

    return tripTrackingParams
  }

  /**
   * Dates need to be unix timestamp in cio
   */
  const cioDateParams = (departureDate: string, returnDate?: string) => ({
    departure_date: departureDate,
    departure_date_ts: dayjs(departureDate).unix(),
    ...(returnDate && {
      return_date: returnDate,
      return_date_ts: dayjs(returnDate).unix(),
    }),
  })

  /**
   * GA4, Meta and TikTok only ever get non-reversible hashes, never the raw email or phone number
   */
  const identifyHashedUser = ({ emailHashed, phoneHashed, phoneE164Hashed }: HashedIdentity) => {
    if (emailHashed) {
      identifyGtagUser(emailHashed)
    }

    if (phoneHashed) {
      identifyGtagPhone(phoneHashed)
    }

    if (FB_ID) {
      identifyFbqUser(FB_ID, { emailHashed, phoneHashed })
    }

    identifyTtqUser({ emailHashed, phoneE164Hashed })
  }

  /**
   * Re-identifies a returning visitor from the hashes stored by identifyUser and identifyPhone,
   * so the identity survives page loads the same way Customer.io's own cookie does
   */
  const identifyStoredUser = () => {
    const identity = getStoredIdentifiedUser()
    if (!identity) {
      return
    }

    identifyHashedUser(identity)
  }

  /**
   * The GA4, Meta and TikTok part of identifyUser. It doesn't wait for the Customer.io script,
   * so an event tracked once it resolves already carries the identity
   */
  const hashAndIdentifyUser = async (email: string, phone?: PhoneNumber) => {
    if (isUserReseller.value) {
      return
    }

    const emailHashed = await hashEmail(email)
    const hashedPhone = phone?.number ? await hashPhone(phone) : {}
    identifyHashedUser(storeIdentifiedUser({ emailHashed, ...hashedPhone }))
  }

  /**
   * Adds a phone number to the visitor's Meta and TikTok matching without passing it to Customer.io,
   * which only gets the phone number of a booking's responsible. A string is the number as
   * typed in a field without a country select: a country code typed with it is kept, and a
   * number typed without one is read as one of the site's country.
   */
  const identifyPhone = async (phone: string | PhoneNumber) => {
    const phoneNumber = typeof phone === 'string'
      ? { countryCode: defaultPhoneCountryCode, number: phone }
      : phone

    if (isUserReseller.value || !phoneNumber.number) {
      return
    }

    identifyHashedUser(storeIdentifiedUser(await hashPhone(phoneNumber)))
  }

  const identifyUser = async (
    email: string,
    { phone, name }: IdentifyUserOptions = {}
  ) => {
    if (!email) {
      $sentryCaptureMessage('Tried to identify user with an undefined email', {
        extra: {
          phone,
          name,
        },
      })
      return
    }

    if (isUserReseller.value) {
      console.info('[Tracking] User is a reseller - Resettting CIO identity')
      clearIdentifiedUserHash()
      await resetCio()
      return
    }

    if (process.env.NODE_ENV === 'development') {
      console.info(
        '[Tracking] Identify user',
        { email, phone, name }
      )
    }

    await hashAndIdentifyUser(email, phone)

    await identifyCio(email, {
      email,
      phone: phone && `+${phone.countryCode}-${phone.number}`,
      name,
    })
  }

  const trackClick = (data: Record<string, string | number | boolean>): void => {
    trackGtag('event', 'click', data)
  }

  const trackModeSwitch = (params: {
    from: string
    to: string
    source: string
  }): void => {
    const { from, to, source } = params

    if (process.env.NODE_ENV === 'development') {
      console.info('[Tracking] Mode switch event', params)
    }

    trackGtag('event', 'mode_switch', {
      from,
      to,
      source,
      switch_count: incrementModeSwitchCount(),
    })
  }

  const trackCatalogueOrder = async (catalogueNames: string[], email?: string): Promise<void> => {
    if (email) {
      await hashAndIdentifyUser(email)
    }

    trackGtag(
      'event',
      'catalogue_order',
      { catalogues: catalogueNames.join('; ') }
    )

    if (email) {
      await identifyUser(email)
    }

    trackCio('catalogue_order', { catalogues: catalogueNames })
  }

  const trackNewsletterSignup = async (airport: string, email?: string): Promise<void> => {
    if (email) {
      await hashAndIdentifyUser(email)
    }

    trackGtag('event', 'newsletter_signup', { airport })

    if (email) {
      await identifyUser(email)
    }
  }

  const trackPageView = (params: PageViewEventParams) => {
    const {
      path,
      url,
      isInitial,
      isExternal,
    } = params

    if (!isInitial && (!path && !url)) {
      $sentryCaptureMessage(
        `Empty ${path ? 'path' : 'url'} "${path || url}" in page view event`,
        { extra: { params } }
      )
    }

    if (process.env.NODE_ENV === 'development') {
      console.info('[Tracking] Page view event', params)
    }

    const eventId = generateTrackingEventId()

    const productArea = path
      ? classifyProductArea(path, localeURLs.value)
      : null

    trackGtag(
      'event',
      'page_view',
      {
        event_id: eventId,
        page_location: path
          ? window.location.origin + path
          : url,
        page_referrer: document.referrer,
        ...(productArea && { product_area: productArea }),
      }
    )

    if (isExternal) {
      return
    }

    /**
     * Record the product area in the session state for internal page views.
     * Placed after the isExternal guard so outgoing links (e.g. video intro) never
     * count as a solresor/group trip visit.
     */
    if (path) {
      registerPageView(path)
    }

    /**
     * Customer.io script tracks initial page view automatically
     */
    if (!isInitial) {
      pageCio()
    }

    if (agentCookie.value) {
      return
    }

    trackFbq(
      'track',
      'PageView',
      {},
      { eventID: eventId }
    )

    pageTtq()
  }

  const trackViewContent = (params: ProductEventParams) => {
    const {
      name,
      id,
      value,
      currency = 'SEK',
      categories,
      departureDate,
      airport,
      trip,
    } = params

    const emptyRequiredValues = getEmptyValuesKeys({ name, id })
    if (emptyRequiredValues) {
      $sentryCaptureMessage(
        `Empty params "${emptyRequiredValues}" in view content event`,
        { extra: { params } }
      )
    }

    if (process.env.NODE_ENV === 'development') {
      console.info('[Tracking] View content event', params)
    }

    const { googleAnalytics: gaCategories, joined: joinedCategory } = normalizeCategories(categories)

    const { contentId, contentType } = getTrackingContent(id, airport)

    const eventId = generateTrackingEventId()

    trackGtag('event', 'view_item', {
      currency,
      value,
      event_id: eventId,
      ...gaCategories,
      items: [{
        item_id: contentId,
        item_name: name,
        affiliation: locale.value,
        ...gaCategories,
        ...(departureDate && { item_variant: departureDate }),
      }],
    })

    trackCio(
      'product_viewed',
      {
        product_id: id,
        product_name: name,
        categories,
        price: value,
        currency,
        ...(airport && { airport }),
        ...(departureDate && cioDateParams(departureDate)),
        ...(trip && tripToTrackingParams(trip)),
        items: [{
          product_id: id,
          product_name: name,
          price: value,
          currency,
          ...(airport && { airport }),
          ...(departureDate && cioDateParams(departureDate)),
          ...(trip && tripToTrackingParams(trip)),
        }],
      }
    )

    if (agentCookie.value) {
      return
    }

    trackFbq(
      'track',
      'ViewContent',
      {
        value,
        currency,
        content_type: contentType,
        content_ids: [contentId],
        content_name: name,
        content_category: joinedCategory,
        ...(departureDate && { content_variant: departureDate }),
      },
      { eventID: eventId }
    )

    trackTtq(
      'ViewContent',
      {
        value,
        currency,
        content_type: contentType,
        contents: [{
          content_id: contentId,
          content_name: name,
          content_category: joinedCategory,
        }],
      }
    )
  }

  const trackViewCategory = (category: Category, tripIds: (number | string)[] = []) => {
    const { id: categoryId, title: categoryName } = category

    const contentIds = (tripIds.length ? tripIds : (category.trips ?? []).map(({ id }) => id))
      .map(String)

    if (process.env.NODE_ENV === 'development') {
      console.info('[Tracking] View category event', category)
    }

    const { googleAnalytics: gaCategories, joined: joinedCategory } = normalizeCategories(categoryName)

    const eventId = generateTrackingEventId()

    /**
     * Sent as GA4 `view_item` (not `view_item_list`) so the Conversions API
     * tag maps it to a Meta `ViewContent` and dedups against the browser pixel
     * via the shared event_id. Trip ids in `items` become the catalog
     * `content_ids` that drive Advantage+ retargeting.
     */
    trackGtag('event', 'view_item', {
      event_id: eventId,
      item_list_id: String(categoryId),
      item_list_name: categoryName,
      ...gaCategories,
      items: contentIds.map((itemId) => ({
        item_id: itemId,
        affiliation: locale.value,
        ...gaCategories,
      })),
    })

    if (agentCookie.value) {
      return
    }

    trackFbq(
      'track',
      'ViewContent',
      {
        content_type: 'product_group',
        content_ids: contentIds,
        content_name: categoryName,
        content_category: joinedCategory,
      },
      { eventID: eventId }
    )
  }

  const trackSearch = (query: string) => {
    const emptyRequiredValues = getEmptyValuesKeys({ query })
    if (emptyRequiredValues) {
      $sentryCaptureMessage(
        `Empty params "${emptyRequiredValues}" in search event`,
        { extra: { query } }
      )
      return
    }

    if (process.env.NODE_ENV === 'development') {
      console.info('[Tracking] Search event', { query })
    }

    const eventId = generateTrackingEventId()

    trackGtag('event', 'search', {
      event_id: eventId,
      search_term: query,
    })

    if (agentCookie.value) {
      return
    }

    trackFbq(
      'track',
      'Search',
      { search_string: query },
      { eventID: eventId }
    )
  }

  /**
   * Cards hand over the same link they render, which is a path on the group sites and
   * an absolute url for Solresor packages. Customer.io needs a clickable absolute uri.
   */
  const toAbsoluteTrackingUri = (url: string): string =>
    url.startsWith('/')
      ? localeURLs.value.BASE_URL + url
      : url

  /**
   * Customer.io payload for a favourite change. The single-product `items` array
   * mirrors the other product events so the same Customer.io templates can be reused.
   */
  const favouriteToCioParams = (params: FavouriteEventParams) => {
    const {
      id,
      name,
      value,
      currency = 'SEK',
      categories,
      url,
      trip,
      productType,
    } = params

    const product = {
      product_id: id,
      ...(name && { product_name: name }),
      ...(value && { price: value, currency }),
      ...(productType && { product_type: productType }),
      ...(url && { uri: toAbsoluteTrackingUri(url) }),
      ...(trip && tripToTrackingParams(trip)),
    }

    return {
      ...product,
      ...(categories?.length && { categories }),
      items: [product],
    }
  }

  /**
   * Favouriting and unfavouriting share their payload - only the event names differ.
   */
  const trackFavouriteChange = (params: FavouriteEventParams, isFavourited: boolean) => {
    const { id, name, value, currency = 'SEK', categories } = params

    const eventLabel = isFavourited
      ? 'add to favourites'
      : 'remove from favourites'

    const emptyRequiredValues = getEmptyValuesKeys({ id })
    if (emptyRequiredValues) {
      $sentryCaptureMessage(
        `Empty params "${emptyRequiredValues}" in ${eventLabel} event`,
        { extra: { params } }
      )
      return
    }

    if (process.env.NODE_ENV === 'development') {
      console.info(`[Tracking] ${eventLabel} event`, params)
    }

    const { googleAnalytics: gaCategories, joined: joinedCategory } = normalizeCategories(categories)

    const { contentId, contentType } = getTrackingContent(id)

    const eventId = generateTrackingEventId()

    trackGtag(
      'event',
      isFavourited
        ? 'add_to_wishlist'
        : 'remove_from_wishlist',
      {
        event_id: eventId,
        ...(value && { currency, value }),
        ...gaCategories,
        items: [{
          item_id: contentId,
          affiliation: locale.value,
          ...(name && { item_name: name }),
          ...gaCategories,
        }],
      }
    )

    trackCio(
      isFavourited
        ? 'product_favourited'
        : 'product_unfavourited',
      favouriteToCioParams(params)
    )

    /**
     * Meta has no standard counterpart to AddToWishlist for removals, and a custom one
     * would only add noise to the pixel's optimisation data, so removals skip Meta.
     */
    if (!isFavourited || agentCookie.value) {
      return
    }

    trackFbq(
      'track',
      'AddToWishlist',
      {
        content_type: contentType,
        content_ids: [contentId],
        content_name: name,
        content_category: joinedCategory,
        ...(value && { currency, value }),
      },
      { eventID: eventId }
    )
  }

  const trackAddToCart = (params: BookingProductEventParams) => {
    const { tripinfo, market } = params

    const productParams = tripInfoToProductParams(tripinfo)
    const {
      name,
      id,
      value,
      categories,
      departureDate,
      returnDate,
      airport,
      tripDepartureId,
      isDeparturePromoted,
      trip,
    } = productParams

    /**
     * Price comes from priceSek
     */
    const CURRENCY = 'SEK'

    const emptyRequiredValues = getEmptyValuesKeys({ name, id })

    if (emptyRequiredValues) {
      $sentryCaptureMessage(
        `Empty params "${emptyRequiredValues}" in add to cart event`,
        { extra: { productParams } }
      )
    }

    if (process.env.NODE_ENV === 'development') {
      console.info('[Tracking] Add to cart event', { params, productParams })
    }

    const { googleAnalytics: gaCategories, joined: joinedCategory } = normalizeCategories(categories)

    const { contentId, contentType } = getTrackingContent(id, airport, isDeparturePromoted, tripDepartureId)

    const eventId = generateTrackingEventId()

    const productArea = market ? productAreaForMarket(market) : null

    trackGtag('event', 'add_to_cart', {
      currency: CURRENCY,
      value,
      event_id: eventId,
      ...gaCategories,
      ...(productArea && { product_area: productArea }),
      items: [
        {
          item_id: contentId,
          item_name: name,
          affiliation: locale.value,
          ...gaCategories,
          ...(productArea && { item_brand: getItemBrandForProductArea(productArea) }),
          ...(departureDate && { item_variant: departureDate }),
        },
      ],
    })

    trackCio(
      'product_added',
      {
        product_id: id,
        product_name: name,
        categories,
        price: value,
        currency: CURRENCY,
        ...(airport && { airport }),
        ...(departureDate && returnDate && cioDateParams(departureDate, returnDate)),
        ...(trip && tripToTrackingParams(trip)),
        items: [{
          product_id: id,
          product_name: name,
          price: value,
          currency: CURRENCY,
          ...(airport && { airport }),
          ...(departureDate && returnDate && cioDateParams(departureDate, returnDate)),
          ...(trip && tripToTrackingParams(trip)),
        }],
      }
    )

    if (agentCookie.value) {
      return
    }

    trackFbq(
      'track',
      'AddToCart',
      {
        value,
        currency: CURRENCY,
        content_type: contentType,
        content_ids: [contentId],
        content_name: name,
        content_category: joinedCategory,
        ...(departureDate && { content_variant: departureDate }),
      },
      { eventID: eventId }
    )

    trackTtq(
      'AddToCart',
      {
        value,
        currency: CURRENCY,
        content_type: contentType,
        contents: [{
          content_id: contentId,
          content_name: name,
          content_category: joinedCategory,
        }],
      }
    )
  }

  const trackInitiateCheckout = (params: BookingProductEventParams) => {
    const { tripinfo, market } = params

    const productParams = tripInfoToProductParams(tripinfo)
    const {
      name,
      id,
      value,
      categories,
      departureDate,
      returnDate,
      airport,
      tripDepartureId,
      isDeparturePromoted,
      trip,
    } = productParams

    /**
     * Price comes from priceSek
     */
    const CURRENCY = 'SEK'

    const emptyRequiredValues = getEmptyValuesKeys({ name, id })
    if (emptyRequiredValues) {
      $sentryCaptureMessage(
        `Empty params "${emptyRequiredValues}" in initiate checkout event`,
        { extra: { productParams } }
      )
    }

    if (process.env.NODE_ENV === 'development') {
      console.info('[Tracking] Initiate checkout event', { params, productParams })
    }

    const { googleAnalytics: gaCategories, joined: joinedCategory } = normalizeCategories(categories)

    const { contentId, contentType } = getTrackingContent(id, airport, isDeparturePromoted, tripDepartureId)

    const eventId = generateTrackingEventId()

    const productArea = market ? productAreaForMarket(market) : null

    trackGtag('event', 'begin_checkout', {
      currency: CURRENCY,
      value,
      event_id: eventId,
      ...gaCategories,
      ...(productArea && { product_area: productArea }),
      items: [
        {
          item_id: contentId,
          item_name: name,
          affiliation: locale.value,
          ...gaCategories,
          ...(productArea && { item_brand: getItemBrandForProductArea(productArea) }),
          ...(departureDate && { item_variant: departureDate }),
        },
      ],
    })

    trackCio(
      'checkout_started',
      {
        product_id: id,
        product_name: name,
        categories,
        price: value,
        currency: CURRENCY,
        ...(airport && { airport }),
        ...(departureDate && returnDate && cioDateParams(departureDate, returnDate)),
        ...(trip && tripToTrackingParams(trip)),
        items: [{
          product_id: id,
          product_name: name,
          price: value,
          currency: CURRENCY,
          ...(airport && { airport }),
          ...(departureDate && returnDate && cioDateParams(departureDate, returnDate)),
          ...(trip && tripToTrackingParams(trip)),
        }],
      }
    )

    if (agentCookie.value) {
      return
    }

    trackFbq(
      'track',
      'InitiateCheckout',
      {
        content_type: contentType,
        value,
        currency: CURRENCY,
        content_ids: [contentId],
        content_name: name,
        content_category: joinedCategory,
        ...(departureDate && { content_variant: departureDate }),
      },
      { eventID: eventId }
    )

    trackTtq(
      'InitiateCheckout',
      {
        content_type: contentType,
        value,
        currency: CURRENCY,
        contents: [{
          content_id: contentId,
          content_name: name,
          content_category: joinedCategory,
        }],
      }
    )
  }

  const trackPurchase = async (params: PurchaseEventParams) => {
    const {
      transaction,
      items: rawItems,
      log,
      bookinfo,
      market,
      confirmationDepartureDate,
      confirmationReturnDate,
      tripinfo = {},
    } = params

    const productParams = tripInfoToProductParams(tripinfo)
    const {
      categories,
      airport,
      trip,
      tripDepartureId,
      isDeparturePromoted,
      departureDate = confirmationDepartureDate,
      returnDate = confirmationReturnDate,
    } = productParams

    const items = tripInfoToPurchaseItems(tripinfo, rawItems)

    const emptyRequiredValues = getEmptyValuesKeys({
      transaction,
      items,
      returnDate,
      departureDate,
      bookinfo,
      tripinfo,
    })
    if (emptyRequiredValues) {
      log(
        `Empty params "${emptyRequiredValues}" in purchase event`,
        { productParams }
      )
    }

    if (process.env.NODE_ENV === 'development') {
      console.info('[Tracking] Purchase event', { params, productParams, items })
    }

    const emailHashed = transaction.email
      ? await hashEmail(transaction.email)
      : undefined

    const metaMatchParameters = getMetaMatchParameters()

    const userData = {
      ...(transaction.email && {
        email: transaction.email,
        email_hashed: emailHashed,
      }),
      ...metaMatchParameters,
    }

    if (Object.keys(userData).length) {
      pushDataLayer({
        user_data: userData,
      })
    }

    if (transaction.email) {
      await hashAndIdentifyUser(transaction.email)
    }

    const { googleAnalytics: gaCategories, joined: joinedCategory } = normalizeCategories(categories)

    const { contentId, contentType } = getTrackingContent(items[0]?.item_id, airport, isDeparturePromoted, tripDepartureId)

    const eventId = generateTrackingEventId()

    const productArea = market ? productAreaForMarket(market) : null

    try {
      trackGtag('event', 'purchase', {
        transaction_id: transaction.id,
        affiliation: transaction.affiliation,
        value: transaction.revenue,
        currency: transaction.currency,
        event_id: eventId,
        user_data: {
          email_address: emailHashed,
          ...metaMatchParameters,
        },
        new_customer: !bookinfo?.isPreviousCustomer,
        ...gaCategories,
        ...(productArea && { product_area: productArea }),
        items: items.map(item => {
          const { contentId: itemContentId } = getTrackingContent(item.item_id, airport, isDeparturePromoted, tripDepartureId)

          return {
            ...item,
            item_id: itemContentId,
            affiliation: locale.value,
            ...gaCategories,
            ...(productArea && { item_brand: getItemBrandForProductArea(productArea) }),
            ...(departureDate && { item_variant: departureDate }),
          }
        }),
        validOn: returnDate,
      })
    } catch (e) {
      log('google analytics event failed', { params, e })
    }

    (async () => {
      try {
        if (transaction.email) {
          await identifyUser(transaction.email)
        }

        await trackCio(
          'purchase',
          {
            product_id: items[0]?.item_id,
            product_name: items[0]?.item_name,
            categories,
            price: transaction.revenue,
            currency: transaction.currency,
            ...(airport && { airport }),
            ...(departureDate && returnDate && cioDateParams(departureDate, returnDate)),
            ...(trip && tripToTrackingParams(trip)),
            // Purchase-specific properties
            transaction_id: transaction.id,
            revenue: transaction.revenue,
            order_total: transaction.revenue,
            items: items.map(item => ({
              product_id: item.item_id,
              name: item.item_name,
              category: item.item_category,
              price: transaction.revenue / items.length,
              currency: transaction.currency,
              ...(airport && { airport }),
              ...(departureDate && returnDate && cioDateParams(departureDate, returnDate)),
              ...(trip && tripToTrackingParams(trip)),
            })),
            ...(transaction.email && { email: transaction.email }),
          }
        )
      } catch (e) {
        log('customer.io event failed', { params, e })
      }
    })()

    if (!agentCookie.value) {
      try {
        trackFbq(
          'track',
          'Purchase',
          {
            value: transaction.revenue,
            currency: transaction.currency,
            content_ids: [contentId],
            content_type: contentType,
            content_name: items[0]?.item_name,
            contents: items.map(item => {
              const { contentId: itemContentId } = getTrackingContent(item.item_id, airport, isDeparturePromoted, tripDepartureId)

              return {
                id: itemContentId,
                quantity: 1,
                item_price: transaction.revenue / items.length,
              }
            }),
            ...(joinedCategory && {
              content_category: joinedCategory,
            }),
            ...(departureDate && {
              content_variant: departureDate,
            }),
          },
          { eventID: eventId }
        )
      } catch (e) {
        log('facebook pixel event failed', { params, e })
      }

      try {
        trackTtq(
          'PlaceAnOrder',
          {
            value: transaction.revenue,
            currency: transaction.currency,
            content_type: contentType,
            contents: [{
              content_id: contentId,
              content_name: items[0]?.item_name,
              content_category: joinedCategory,
            }],
          }
        )
      } catch (e) {
        log('tiktok analytics event failed', { params, e })
      }

      trackAfterScriptLoad(
        () => !!window.ADT?.Tag?.doEvent,
        () => {
          const adtractionConfig = ADTRACTION as { new?: number; existing?: number } | undefined

          if (
            !('advertising' in userConsentStatus.value) ||
            !userConsentStatus.value.advertising ||
            !bookinfo ||
            !adtractionConfig?.new ||
            !adtractionConfig?.existing
          ) {
            return
          }

          try {
            const discount = (tripinfo.extra_costs || []).find(({ type }) => type === 'discount_code')
            let discountCode
            if (discount && discount.description) {
              discountCode = discount.description.split(' ').pop()
            }

            const { ADT } = window
            ADT!.Tag = ADT!.Tag || {}
            ADT!.Tag.tp = (
              bookinfo.isPreviousCustomer
                ? adtractionConfig.existing
                : adtractionConfig.new
              ) as number
            ADT!.Tag.xd = transaction.email
              ? md5(transaction.email)
              : undefined
            ADT!.Tag.t = 3
            ADT!.Tag.c = transaction.originalCurrency
            ADT!.Tag.am = transaction.originalCurrencyRevenue
            ADT!.Tag.ti = transaction.id
            ADT!.Tag.cpn = discountCode

            window.ADT!.Tag.doEvent()
          } catch (e) {
            log('adtraction event failed', { params, e })
          }
        }
      )
    }

    log(
      'book success',
      {
        params,
        dataLayer: window.dataLayer,
        gtmLoaded: !!window.google_tag_manager,
      },
      'info'
    )
  }

  const trackCustomerDataEmailEntered = async (email: string) => {
    const emptyRequiredValues = getEmptyValuesKeys({ email })
    if (emptyRequiredValues) {
      $sentryCaptureMessage(
        `Empty params "${emptyRequiredValues}" in event customer data email entered`,
        { extra: { email } }
      )
      return
    }

    if (process.env.NODE_ENV === 'development') {
      console.info('[Tracking] Customer data email entered', email)
    }

    await identifyUser(email)

    await trackCio(
      'customer-data-email-entered',
      { email }
    )
  }

  /**
   * Registers waitinglist for triptype if no departureId is passed in
   * If departureId is defined, register departute waitinglist instead
   */
  const trackRegisterWaitinglist = async (
    email: string,
    tripTypeId: number,
    {
      name,
      categories,
      departureId,
      phone,
    }: {
      name?: string,
      categories?: string[],
      departureId?: number,
      phone?: string | PhoneNumber
    } = {}
  ) => {
    const type = departureId ? 'departure' : 'triptype'

    const emptyRequiredValues = getEmptyValuesKeys({ email })
    if (emptyRequiredValues) {
      $sentryCaptureMessage(
        `Empty params "${emptyRequiredValues}" in event register ${type} waitinglist`,
        { extra: { email } }
      )
      return
    }

    if (process.env.NODE_ENV === 'development') {
      console.info(
        `[Tracking] Register ${type} waitinglist`,
        { email, tripTypeId, name, categories }
      )
    }

    await identifyUser(email)

    if (phone) {
      await identifyPhone(phone)
    }

    await trackCio(
      `${type}-waitinglist`,
      {
        product_id: tripTypeId,
        product_name: name,
        categories,
        departureId,
      }
    )
  }

  return {
    trackPageView,
    trackViewContent,
    trackViewCategory,
    trackSearch,
    trackFavouriteChange,
    trackAddToCart,
    trackInitiateCheckout,
    trackClick,
    trackModeSwitch,
    trackCatalogueOrder,
    trackNewsletterSignup,
    trackPurchase,
    identifyUser,
    identifyPhone,
    identifyStoredUser,
    trackCustomerDataEmailEntered,
    trackAfterScriptLoad,
    trackRegisterWaitinglist,
  }
}